import { XMarkIcon } from "@heroicons/react/16/solid";
import React, { useRef, useState } from "react";

import { Tooltip } from "@fronterahealth/frontera-ui-components";

import { UserInitialsView } from "@components/UserInitial/UserInitialsView";

enum HoverTarget {
  None = "none",
  Avatar = "avatar",
  XButton = "x-button",
}

interface AvatarUploadProps {
  imageUrl: string | null;
  firstName: string;
  lastName: string;
  isLoading?: boolean;
  canRemove?: boolean;
  onFileChange: (file: File) => void;
  onRemove: () => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  imageUrl,
  firstName,
  lastName,
  isLoading,
  canRemove,
  onFileChange,
  onRemove,
}) => {
  const [hoverTarget, setHoverTarget] = useState<HoverTarget>(HoverTarget.None);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onFileChange(file);
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const tooltipText = !imageUrl
    ? "Upload photo"
    : canRemove && hoverTarget === HoverTarget.XButton
      ? "Remove avatar"
      : "Replace avatar";

  if (isLoading) {
    return (
      <div className="relative flex flex-col items-center pb-7">
        <div className="w-28 h-28 rounded-full bg-gray-200 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center pb-7">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      <Tooltip text={tooltipText} showInfoIcon={false} placement="bottom">
        <div
          className="relative w-28 h-28 cursor-pointer"
          onMouseEnter={() => setHoverTarget(HoverTarget.Avatar)}
          onMouseLeave={() => setHoverTarget(HoverTarget.None)}
          onClick={openFilePicker}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" className="w-28 h-28 rounded-full object-cover border" />
          ) : (
            <UserInitialsView
              firstName={firstName}
              lastName={lastName}
              className="w-28! h-28! max-w-none! max-h-none! text-4xl!"
            />
          )}

          {/* X button — only visible when a deletable photo exists and avatar is hovered */}
          {canRemove && hoverTarget !== HoverTarget.None && (
            <button
              type="button"
              aria-label="Remove avatar"
              className="absolute top-0 right-0 flex items-center justify-center w-6 h-6 rounded-full bg-bg-surface border border-border-medium shadow-xs hover:bg-bg-hovered transition-colors"
              onMouseEnter={(e) => {
                e.stopPropagation();
                setHoverTarget(HoverTarget.XButton);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                setHoverTarget(HoverTarget.Avatar);
              }}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <XMarkIcon className="w-4 h-4 text-icon-secondary" />
            </button>
          )}
        </div>
      </Tooltip>
    </div>
  );
};
